import { useState } from 'react';
import PropTypes from 'prop-types';
// @mui
import { styled } from '@mui/material/styles';
import { Box, AppBar, Toolbar, IconButton, Menu, MenuItem, Typography } from '@mui/material';
// components
import Iconify from '../../../components/iconify';
import { HeaderNav } from '../../../components/header-nav';
import { ScopeSelector } from '../../../components/scope-selector';
import { useProject } from '../../../hooks/ProjectContext';

// ----------------------------------------------------------------------

const HEADER_HEIGHT = 64;

const StyledRoot = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'navWidth',
})(({ theme, navWidth }) => ({
  backgroundColor: '#FFFFFF',
  boxShadow: 'none',
  borderBottom: '1px solid #DFE3E8',
  [theme.breakpoints.up('lg')]: {
    width: `calc(100% - ${navWidth + 1}px)`,
  },
}));

const StyledToolbar = styled(Toolbar)({
  minHeight: HEADER_HEIGHT,
  padding: '0 24px',
});

// ----------------------------------------------------------------------

Header.propTypes = {
  onOpenNav: PropTypes.func,
  onToggleNavCollapse: PropTypes.func,
  isNavCollapsed: PropTypes.bool,
  navWidth: PropTypes.number,
  pageTitle: PropTypes.string,
  visualMode: PropTypes.bool,
  onExitVisualMode: PropTypes.func,
};

export default function Header({ onOpenNav, onToggleNavCollapse, isNavCollapsed, navWidth, pageTitle = 'Project', visualMode, onExitVisualMode }) {
  const { availableProjects, selectedProject, setSelectedProject, availableSites, selectedSite, setSelectedSite } = useProject();
  const [, setState] = useState(0); // force re-render after menu close

  return (
    <StyledRoot navWidth={navWidth}>
      <StyledToolbar>
        {visualMode ? (
          <IconButton onClick={onExitVisualMode} sx={{ mr: 1, p: 0.5 }}>
            <Box component="img" src="/logo/logo_single.svg" sx={{ width: 36, height: 36 }} />
          </IconButton>
        ) : (
          <>
            <IconButton onClick={onOpenNav} sx={{ mr: 1, color: 'text.primary', display: { lg: 'none' } }}>
              <Iconify icon="eva:menu-2-fill" />
            </IconButton>
            <IconButton onClick={onToggleNavCollapse}
              sx={{ mr: 1, color: 'text.primary', display: { xs: 'none', lg: 'inline-flex' } }}
              aria-label={isNavCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
              <Iconify icon={isNavCollapsed ? 'eva:arrow-ios-forward-fill' : 'eva:arrow-ios-back-fill'} />
            </IconButton>
          </>
        )}

        {/* Breadcrumb-style scope: Project / Site */}
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
          <ScopeSelector
            label="Project..."
            value={selectedProject?.name}
            options={availableProjects}
            onChange={proj => { setSelectedProject(proj); setState(n => n + 1); }}
          />
          {selectedProject && (
            <>
              <Typography variant="caption" color="text.disabled" sx={{ mx: 0.5 }}>/</Typography>
              <ScopeSelector
                label="All sites"
                value={selectedSite?.name}
                options={[{ id: null, name: 'All sites' }, ...availableSites]}
                onChange={site => { setSelectedSite(site.id ? site : null); setState(n => n + 1); }}
              />
            </>
          )}
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        <HeaderNav settingsPath="/project/settings" />
      </StyledToolbar>
    </StyledRoot>
  );
}
